import { ApplicationRef, Injectable, NgZone, OnDestroy } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { IdleService } from 'core-client/api';
import { Subscription, concat, timer } from 'rxjs';
import { distinctUntilChanged, filter, first, ignoreElements, skip, throttleTime } from 'rxjs/operators';

/** How often to poll for application updates (in milliseconds). */
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

/** Minimum time between visibility-triggered checks to avoid rapid-fire requests. */
const VISIBILITY_THROTTLE_MS = 60 * 1000; // 1 minute

@Injectable({
  providedIn: 'root',
})
export class AppVersionsUpdateService implements OnDestroy {
  private monitoringSub?: Subscription;

  constructor(
    private readonly swUpdate: SwUpdate,
    private readonly snackBar: MatSnackBar,
    private readonly translator: TranslateService,
    private readonly ngZone: NgZone,
    private readonly appRef: ApplicationRef,
    private readonly idleService: IdleService,
  ) {}

  /**
   * Start monitoring for application updates.
   * - Subscribes to version update and unrecoverable state events
   * - Waits for app stability, then polls checkForUpdate() on a configurable interval
   * - Checks when the browser tab becomes visible (via IdleService)
   *
   * All timers run outside NgZone to avoid triggering change detection.
   */
  startMonitoring(pollIntervalMs: number = UPDATE_CHECK_INTERVAL_MS): void {
    if (!this.swUpdate.isEnabled) {
      console.log('SwUpdate is not enabled — skipping update monitoring.');
      return;
    }

    this.stopMonitoring();
    this.monitoringSub = new Subscription();

    this.monitoringSub.add(
      this.swUpdate.versionUpdates.subscribe((evt) => {
        switch (evt.type) {
          case 'VERSION_DETECTED':
            console.log(`Downloading new app version: ${evt.version.hash}`);
            break;
          case 'VERSION_READY':
            console.log(`Current app version: ${evt.currentVersion.hash}`);
            console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
            this.promptUser('NEW_ANGULAR_UPDATE');
            break;
          case 'VERSION_INSTALLATION_FAILED':
            console.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
            this.promptUser('NEW_ANGULAR_UPDATE_ERROR');
            break;
        }
      }),
    );

    this.monitoringSub.add(
      this.swUpdate.unrecoverable.subscribe((evt) => {
        console.error(`Service worker unrecoverable state: ${evt.reason}`);
        this.promptUser('NEW_ANGULAR_UPDATE_ERROR');
      }),
    );

    // Run polling outside NgZone to avoid change detection
    this.ngZone.runOutsideAngular(() => {
      // Wait for app stability (ensures SW is registered), then check immediately + poll.
      // ignoreElements() gates on stability without forwarding the boolean as its own
      // check — otherwise the stability value and timer(0) would each fire, double-checking.
      const stable$ = this.appRef.isStable.pipe(
        first((stable) => stable),
        ignoreElements(),
      );
      this.monitoringSub!.add(
        concat(stable$, timer(0, pollIntervalMs)).subscribe(() =>
          this.triggerUpdateCheck(),
        ),
      );
    });

    // Check for updates when the tab becomes visible (idle → active transition).
    // IdleService already runs outside NgZone — no additional zone isolation needed.
    this.monitoringSub.add(
      this.idleService
        .checkIfIdle$()
        .pipe(
          distinctUntilChanged(),
          skip(1), // skip initial emission
          filter((idle) => !idle), // only when becoming active
          throttleTime(VISIBILITY_THROTTLE_MS),
        )
        .subscribe(() => this.triggerUpdateCheck()),
    );
  }

  stopMonitoring(): void {
    this.monitoringSub?.unsubscribe();
    this.monitoringSub = undefined;
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
  }

  private triggerUpdateCheck(): Promise<boolean> {
    return this.swUpdate.checkForUpdate().catch((err) => {
      console.error('Failed to check for updates:', err);
      return false;
    });
  }

  private promptUser(messageKey: string): void {
    this.ngZone.run(() => {
      const snackbarRef = this.snackBar.open(
        this.translator.instant(messageKey),
        'Reload',
        {
          panelClass: 'blue-snackbar',
          verticalPosition: 'bottom',
          horizontalPosition: 'center',
        },
      );

      snackbarRef.onAction().subscribe(() => {
        window.location.reload();
      });
    });
  }
}
